import { useCallback, useState } from 'react';
import { getDashboardConfig, saveDashboardConfig } from '../services/api/dashboardConfig.api';
import { supabase } from '../lib/supabase';
import { upsertUserDashboardState } from '../services/userDashboardState';

// Per-browser dashboard config: localStorage is the authoritative copy so
// every browser keeps its own tabs/layouts. loadConfig() itself never reaches
// past localStorage once a copy exists (the server config only seeds a
// brand-new browser's first visit) — a NEWER save made on another device is
// instead pulled in via the separate syncFromServerIfNewer() step below, for
// logged-in users only, called once at app mount (see App.jsx). This never
// changes which OCEL file is active — applyConfig() (App.jsx) only ever
// applies tabs/components/layouts/component_settings, never config.active_file.
// Key is namespaced by userId so different users on the same browser each
// get their own isolated config. Falls back to the legacy key when no user
// is available (dev env with AUTH_ENABLED=false).
const BASE_KEY = 'cintelion.dashboardConfig';
const storageKey = (userId) => (userId ? `${BASE_KEY}.${userId}` : BASE_KEY);

const readLocalConfig = (userId) => {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

// Throws on quota/serialization errors — callers decide whether that's fatal.
const writeLocalConfig = (userId, config) => {
  localStorage.setItem(storageKey(userId), JSON.stringify(config));
};

export function useDashboardConfig(userId) {
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const loadConfig = useCallback(async () => {
    const local = readLocalConfig(userId);
    if (local) return local;

    try {
      const serverConfig = await getDashboardConfig();
      if (serverConfig) {
        // Seed this browser with the shared template (best-effort)
        try {
          writeLocalConfig(userId, serverConfig);
        } catch {
          // localStorage unavailable — config just won't survive a reload
        }
      }
      return serverConfig;
    } catch (e) {
      console.warn('Could not load dashboard config:', e.message);
      return null;
    }
  }, [userId]);

  const saveConfig = useCallback(async (payload) => {
    setIsSaving(true);
    setSaveError(null);
    try {
      writeLocalConfig(userId, payload);
      // Best-effort: keep the server template up to date for new browsers,
      // and (for a logged-in user) record when it happened so other devices
      // can tell their own local copy is stale — see syncFromServerIfNewer.
      saveDashboardConfig(payload)
        .then((res) => {
          // Stamp the local copy with the server's saved_at too, so this same
          // device's own next mount recognizes it's already current instead
          // of re-fetching the config it just saved.
          try {
            writeLocalConfig(userId, { ...payload, saved_at: res.saved_at });
          } catch {
            // localStorage unavailable — next mount just re-fetches, harmless
          }
          supabase.auth
            .getSession()
            .then(({ data: { session } }) => {
              if (!session?.user) return;
              return upsertUserDashboardState({ dashboard_config_saved_at: res.saved_at });
            })
            .catch(() => {});
        })
        .catch((e) => console.warn('Could not save dashboard config to server:', e.message));
      return payload;
    } catch (e) {
      setSaveError(e.message);
      throw e;
    } finally {
      setIsSaving(false);
    }
  }, [userId]);

  // Cross-device pull: if a logged-in user's OTHER device saved a newer
  // config (dashboard_config_saved_at from user_dashboard_state) than what
  // this browser has cached locally, fetch the fresh copy and overwrite the
  // local cache with it. Mirrors how last_active_file_set_at already
  // arbitrates the active-file case. Called once at app mount, right after
  // loadConfig() — never blocks startup on failure, never runs mid-session.
  const syncFromServerIfNewer = useCallback(async (localConfig, serverSavedAt) => {
    if (!serverSavedAt) return localConfig;
    const localSavedAt = localConfig?.saved_at;
    if (localSavedAt && new Date(localSavedAt) >= new Date(serverSavedAt)) return localConfig;
    try {
      const fresh = await getDashboardConfig();
      if (fresh) {
        writeLocalConfig(userId, fresh);
        return fresh;
      }
    } catch (e) {
      console.warn('Could not sync dashboard config from server:', e.message);
    }
    return localConfig;
  }, [userId]);

  return { loadConfig, saveConfig, syncFromServerIfNewer, isSaving, saveError };
}
